import { 
    AppBar, 
    Avatar, 
    Grid2, 
    Badge, 
    CircularProgress, 
    Menu, 
    MenuItem, 
    Toolbar, 
    Box, 
    Paper, 
    Popover, 
    List, 
    ListItem, 
    ListItemText, 
    Divider, 
    Typography, 
    Button,
    useTheme
} from '@mui/material';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import './navbar.css';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Logo from '../../assets/images/logo.png';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import chatNotification from '../../assets/icons/chatnotification.svg';
import notifications from '../../assets/icons/notifications.svg';
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import altAvatar from '../../assets/images/altAvatar.svg';
import config from '../../config/config';
import { makeGetRequestToUrl, makePostRequestToUrl } from '../commonFunc';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/en'; // Import the locale you need

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
      padding: theme.spacing(1, 1, 1, 0),
      // vertical padding + font size from searchIcon
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '20ch',
      },
    },
}));

const getRandomLightColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const pastel = `hsl(${hue}, 100%, 90%)`; // light pastel tone
  return pastel;
};

const Navbar = () => {
    const theme = useTheme();
    const Navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const [unseenNotificationCount, setUnseenNotificationCount] = useState(Number(localStorage.getItem('unseen_notification_count')) || 0);
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const [notificationsList, setNotificationsList] = useState([]);
    const [brandLogo, setBrandLogo] = useState(localStorage.getItem('client_brand_logo') || Logo);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const open = Boolean(anchorEl);
    const utcOffsetVal = parseInt(localStorage.getItem('utc_offset') || "0", 10);

    
    // Listen for changes to the logo in localStorage
    useEffect(() => {
        // Function to update logo from localStorage
        const updateLogoFromStorage = () => {
            const newBrandLogo = localStorage.getItem('client_brand_logo');
            setBrandLogo(newBrandLogo || Logo);
        };

        // Create a custom event for logo updates
        window.addEventListener('storage_updated', updateLogoFromStorage);
        
        // Regular interval check as a fallback
        const interval = setInterval(() => {
            const storedLogo = localStorage.getItem('client_brand_logo');
            if (storedLogo !== brandLogo) {
                setBrandLogo(storedLogo || Logo);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage_updated', updateLogoFromStorage);
            clearInterval(interval);
        };
    }, [brandLogo]);
    
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = (event) => {
        updateNotificationCount();
        fetchNotifications();

        // 🧠 Safely reset unseen notification count
        const newCount = 0;
        localStorage.setItem("unseen_notification_count", String(newCount)); // always store as string
        window.dispatchEvent(new CustomEvent("unseen_count_updated", { detail: newCount }));

        // 🧩 Also update React state immediately so badge updates instantly
        setUnseenNotificationCount(newCount);

        setNotificationAnchorEl(event.currentTarget);
    };

    const handleSingleNotificationClick = (notification) => {

        if (notification.flag === 2000) {
            Navigate('/mapview?engagement_id=' + notification.data?.trip_engagement_id + '&city_id=' + notification.data?.city_id);
            handleNotificationClose(); 
        }
        if(notification.flag === 2001){
            Navigate('/scheduled-bookings?ride_id=' + notification.data?.ride_id + '&city_id=' + notification.data?.city_id);
            handleNotificationClose(); 
        }
        if(notification.flag === 913){
            Navigate('/support-chat?chat_id=' + notification.data?.chat_id + '&user_type=' + notification.data?.user_type );
            handleNotificationClose(); 
        }
        if(notification.flag === 2004){
            Navigate(`/?driver_id=${notification.data.driver_id}&city_id=${notification.data.city_id}&ts=${notification.logged_on}`);  
            handleNotificationClose(); 
        }
    };



    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    const handleViewHistory = () => {
        handleNotificationClose();
        const lastNotificationDate = notificationsList.length > 0 
        ? dayjs(notificationsList[0].logged_on) // assuming most recent is first
        : null;
    
    Navigate('/notification', { 
        state: { 
            initialDate: lastNotificationDate?.format('YYYY-MM-DD') 
        } 
    });
    };

const logoutPanelUser = async () => {
    try {
        // Unregister all service workers
        if ('serviceWorker' in navigator) {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map(reg => reg.unregister()));
                console.log('Service workers unregistered');
            } catch (swError) {
                console.error('Error unregistering service workers:', swError);
            }
        }

        // Clear Firebase tokens if needed
        if (window.firebase) {
            try {
                const { getMessaging, deleteToken } = await import("firebase/messaging");
                const messaging = getMessaging();
                await deleteToken(messaging);
                console.log('Firebase token deleted');
            } catch (firebaseError) {
                console.error('Error cleaning up Firebase:', firebaseError);
            }
        }

        // Clear storage
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
        // Navigate to login
        Navigate('/signup');
    } catch (error) {
        console.error('Error during logout:', error);
        // Still proceed with logout even if there was an error
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
        Navigate('/signup');
    }
};

    const formatNotificationTime = (dateString) => {
        if (!dateString) return "";

        const localDate = dayjs(dateString);   // No custom UTC offset

        const now = dayjs();

        const diffInDays = now.startOf("day").diff(localDate.startOf("day"), "day");

        if (diffInDays === 0) return "Today";
        if (diffInDays === 1) return "Yesterday";
        if (diffInDays < 7) return localDate.format("dddd");

        return localDate.format("MMM D, YYYY");
    };



    const formatTime = (isoString) => {
        if (!isoString) return "";

        const date = new Date(isoString); // uses browser/system timezone automatically

        const hours = date.getHours();
        const minutes = date.getMinutes();

        const ampm = hours >= 12 ? "pm" : "am";
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = String(minutes).padStart(2, "0");

        return `${formattedHours}:${formattedMinutes} ${ampm}`;
    };



    
    const fetchNotifications = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const url = serverUrl + '/panel/fetch_notifications';
            const body = { token,
                is_limit:1,
             };
            const response = await makeGetRequestToUrl(url, body);
            
            if (response.flag == 200) {
                setNotificationsList(response.data);
                // Update unseen count if needed
                const unseenCount = response.data.filter(notif => notif.flag === 1).length;
                setUnseenNotificationCount(unseenCount);
            } else {
                setNotificationsList([]);
                console.error('Unexpected response format:', response);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to load notifications');
            setNotificationsList([]);
        } finally {
            setIsLoading(false);
        }
    }

    const updateNotificationCount = async() => {
        try{
            const token = localStorage.getItem('token');
            const url = operationsUrl + '/panel/mark_notification_read';
            const body = { token };
            const response = await makePostRequestToUrl(url, body);
            
            if (response.flag == 200) {
                setUnseenNotificationCount(response.data);
            } else {
                console.error('Unexpected response format:', response);
            }
        }catch(err){
            console.error('Error updating notification count:', err);
        }
    }

      useEffect(() => {
    // 🧩 Listen to messages from Service Worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data && event.data.type === "NEW_NOTIFICATION") {
          setUnseenNotificationCount(((prev) => prev + 1));
        }
      });
    }
  }, []);

useEffect(() => {
  const syncCount = () => {
    const storedCount = Number(localStorage.getItem("unseen_notification_count")) || 0;
    setUnseenNotificationCount(storedCount);
  };

  // Listen to both: storage (cross-tab) + custom event (same tab)
  const handleStorageChange = (event) => {
    if (event.key === "unseen_notification_count") syncCount();
  };
  const handleCustomChange = (event) => {
    setUnseenNotificationCount(event.detail || 0);
  };

  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("unseen_count_updated", handleCustomChange);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("unseen_count_updated", handleCustomChange);
  };
}, []);

    return(
        // <Box>
            <AppBar position="static" className="appbarr" sx={{boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)'}}>
                <Toolbar>

                <Box sx={{ 
                    width: "240px", 
                    height: "50px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "flex-start",
                    overflow: "hidden",
                    mr: 2
                }}>
                    <img 
                        src={brandLogo} 
                        alt="Company Logo" 
                        style={{
                            maxWidth: "100%", 
                            maxHeight: "100%", 
                            objectFit: "contain"
                        }}
                    />
                </Box>

                <Search>
                    <SearchIconWrapper>
                    <SearchIcon />
                    </SearchIconWrapper>
                    <StyledInputBase
                    placeholder="Search…"
                    inputProps={{ 'aria-label': 'search' }}
                    />
                </Search>

                <Grid2 container spacing={2} alignItems="center" sx={{ ml: 'auto', width: 'auto' }}>

                    <Grid2 item>
                        <select style={{padding:'12px 24px', border: '1px solid #E6F0FF', borderRadius:'10px'}}>
                            <option value="">Language</option>
                        </select>
                    </Grid2>

                    <Grid2 item>
                        <select style={{padding:'12px 24px', border: '1px solid #E6F0FF', borderRadius:'10px'}}>
                            <option value="">Country</option>
                        </select>
                    </Grid2>

                    <Grid2
                        item
                        onClick={handleNotificationClick}
                        style={{
                            border: '1px solid #E6F0FF',
                            borderRadius: '8px',
                            padding: '10px',
                            color: '#1A1E25',
                            position: 'relative',
                            cursor: 'pointer',
                        }}
                        >
                        <Badge
                            badgeContent={unseenNotificationCount > 0 ? unseenNotificationCount : null}
                            color="error"
                            invisible={unseenNotificationCount === 0}
                            overlap="circular"
                            anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                            }}
                            sx={{
                            "& .MuiBadge-badge": {
                                fontSize: '0.65rem',
                                height: 18,
                                minWidth: 18,
                                borderRadius: '50%',
                                border: '2px solid white',
                            },
                            }}
                        >
                            <img
                            src={notifications}
                            width="22px"
                            height="22px"
                            alt="notification"
                            style={{ cursor: 'pointer' }}
                            />
                        </Badge>
                        </Grid2>


                    <Popover
                        open={Boolean(notificationAnchorEl)}
                        anchorEl={notificationAnchorEl}
                        onClose={handleNotificationClose}
                        anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                        }}
                        transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                        }}
                        PaperProps={{
                        sx: {
                            width: 500,
                            maxHeight: 450,
                            borderRadius: 2,
                            mt: 1.5,
                            p: 1.5,
                            boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
                        },
                        }}
                    >
                        <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            New Notifications
                        </Typography>
                        <Divider sx={{ my: 1 }} />

                        <List sx={{ width: '100%', maxHeight: '300px', overflow: 'auto' }}>
                            {isLoading ? (
                                <Box display="flex" justifyContent="center" p={3}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : error ? (
                                <Typography color="error" p={2}>
                                    {error}
                                </Typography>
                            ) : notificationsList && notificationsList.length > 0 ? (
                                notificationsList.map((notification, index) => (
                                    <Box key={index} onClick={()=>handleSingleNotificationClick(notification)}>
                                        <ListItem
                                            button
                                            sx={{
                                                backgroundColor: notification.flag === 1 ? alpha(theme.palette.primary.light, 0.1) : 'transparent',
                                                borderRadius: '8px',
                                                mb: 1,
                                                transition: '0.3s',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                backgroundColor:
                                                    notification.flag === 1 ? alpha(theme.palette.primary.light, 0.2) : alpha('#000000', 0.05)
                                                },
                                                alignItems: 'center',
                                                py: 1.5,
                                                px: 2,
                                                width: '100%',
                                            }}
                                            >
                                            {/* Main row that fills available space */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                                {/* Left icon */}
                                                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', mr: 1 }}>
                                                <img src={chatNotification} alt="notification" style={{ width: 50, height: 50 }} />
                                                </Box>

                                                {/* Middle content (title + message) */}
                                                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                <Typography variant="subtitle2" fontWeight={600} color="text.primary" noWrap sx={{color: '#1c5bed'}}>
                                                    {notification.title || 'Notification'}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.2, overflow: 'hidden',color: '#83888e', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                                </Box>

                                                {/* Right side timing (always aligned to right) */}
                                                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2, minWidth: 70 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatTime(notification.logged_on)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatNotificationTime(notification.logged_on)}
                                                </Typography>
                                                </Box>
                                            </Box>
                                            </ListItem>

                                        {index < notificationsList.length - 1 && <Divider />}
                                    </Box>
                                ))
                            ) : (
                                <Box p={3} textAlign="center">
                                    <NotificationsOffIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        No notifications yet
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        We'll let you know when there's something new
                                    </Typography>
                                </Box>
                            )}
                        </List>

                        <Divider sx={{ my: 1 }} />
                        <Button fullWidth color="primary" onClick={handleViewHistory}>
                            View Full History
                        </Button>
                        </Box>
                    </Popover>
                    <Grid2 item sx={{border: '1px solid #E6F0FF', borderRadius:'18px'}}>
                        <Avatar alt="profile" src={altAvatar} aria-controls={open ? 'demo-positioned-menu' : undefined}
                         aria-haspopup="true" aria-expanded={open ? 'true' : undefined} onClick={handleClick}/>


                        <Menu id="demo-positioned-menu" aria-labelledby="demo-positioned-button"
                         anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={{vertical:'top', horizontal:'left'}}
                            transformOrigin={{ vertical: 'top', horizontal: 'left'}}>
                            {/* <MenuItem onClick={handleClose}>Profile</MenuItem>
                            <MenuItem onClick={handleClose}>My account</MenuItem> */}
                            <MenuItem onClick={() => {
                                logoutPanelUser().catch(console.error);
                            }}>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Grid2>
                    
                </Grid2>
                </Toolbar>
            </AppBar>
        // </Box>
    )
};

export default Navbar;